import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import {
  filterTextFieldOptions,
  type TextFieldOption,
} from './collectTextFieldOptions';
import { formatSectionDropdownLabel } from '../formSchema/formatSectionDropdownLabel';

export interface FormFieldComboboxProps {
  options: TextFieldOption[];
  /** Selected question id, or empty when nothing is chosen yet. */
  value: string;
  onValueChange: (qid: string) => void;
  disabled?: boolean;
  /** Shown on the trigger when nothing is selected, and as the empty reason. */
  placeholder: string;
  /**
   * `section_id` to the heading the form itself uses, from the schema version's
   * `section_order`. A section with no entry falls back to its key.
   */
  sectionLabels?: Record<string, string>;
}

/**
 * Searchable picker for the form question a template targets.
 *
 * A plain dropdown was unusable here: a form carries dozens of text questions,
 * their prompts are full sentences, and an admin scrolling for one they half
 * remember has nothing to narrow the list with. This filters as they type.
 *
 * The question id is searched but not shown. An admin picks a field by reading
 * its wording, and a column of hashes beside every row is noise to them. It
 * stays searchable because an id is what a spec or a support thread names, so
 * somebody arriving with one still has to be able to find the field it means.
 *
 * Options are grouped under the form section they sit in, so an admin can find
 * a field by where they remember it on the form rather than reading the whole
 * list. The headings are dropped when there is only one, which is what a form
 * whose templatable fields all sit together, or any non-OASIS form, comes back
 * as.
 *
 * cmdk's own filtering is turned off. It scores against one value per item, so
 * it cannot match a prompt and an id together, and its ranking would reorder
 * the sections the admin is scanning.
 */
export function FormFieldCombobox({
  options,
  value,
  onValueChange,
  disabled = false,
  placeholder,
  sectionLabels = {},
}: FormFieldComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(
    () => filterTextFieldOptions(options, query),
    [options, query]
  );

  // Consecutive runs rather than a keyed bucket: `collectTextFieldOptions`
  // already emits the sections in the order the form presents them, and
  // filtering only removes options, so a run is still a whole section.
  const groups = useMemo(() => {
    const built: { section: string; options: TextFieldOption[] }[] = [];
    for (const option of filtered) {
      const last = built[built.length - 1];
      if (last && last.section === option.section) {
        last.options.push(option);
      } else {
        built.push({ section: option.section, options: [option] });
      }
    }
    return built;
  }, [filtered]);

  // One heading over the whole list says nothing. Non-OASIS forms arrive as a
  // single merged section, and a form can also have all its templatable fields
  // in one place, so this is the common case rather than an edge.
  const showHeadings = groups.length > 1;
  const selected = options.find(option => option.qid === value);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Start from the whole list each time it opens. A query left over from a
    // previous visit would look like a form with almost no fields on it.
    if (!next) setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          role='combobox'
          aria-expanded={open}
          disabled={disabled}
          className='w-full justify-between font-normal'
        >
          {/* Truncated because a prompt is a whole sentence and the trigger is
              one line; the full text is on the row in the list. */}
          <span className='truncate text-left'>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-[--radix-popover-trigger-width] p-0'
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder='Search by wording or question id'
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>No field matches that search.</CommandEmpty>
            {groups.map(group => (
              <CommandGroup
                key={group.section}
                heading={
                  showHeadings
                    ? formatSectionDropdownLabel(group.section, sectionLabels)
                    : undefined
                }
              >
                {group.options.map(option => (
                  <CommandItem
                    key={option.qid}
                    value={option.qid}
                    onSelect={() => {
                      onValueChange(option.qid);
                      handleOpenChange(false);
                    }}
                    className='items-start gap-2'
                  >
                    <Check
                      className={`mt-0.5 h-4 w-4 shrink-0 ${
                        value === option.qid ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                    {/* Same reach as the table's rows: two questions can
                        carry identical wording, and the id settles which is
                        which without putting a hash on every row. */}
                    <span
                      className='min-w-0 whitespace-normal'
                      title={option.qid}
                    >
                      {option.label}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
